import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Dispatch } from 'redux';
import { Employee, Position, Tag } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';
import { ErrorMessage } from '../../../../../../common/models/enums/error-message';

export const SUPPLIER_EMPLOYEES_ADD = 'SUPPLIER_EMPLOYEES_ADD';
export const SUPPLIER_EMPLOYEES_REMOVE = 'SUPPLIER_EMPLOYEES_REMOVE';
export const SUPPLIER_EMPLOYEES_REMOVE_SUCCESS = 'SUPPLIER_EMPLOYEES_REMOVE_SUCCESS';
export const SUPPLIER_EMPLOYEES_REMOVE_FAILED = 'SUPPLIER_EMPLOYEES_REMOVE_FAILED';
export const SUPPLIER_EMPLOYEES_REMOVE_FAILED_WITH_BONUS_CONDITION_ERROR =
    'SUPPLIER_EMPLOYEES_REMOVE_FAILED_WITH_BONUS_CONDITION_ERROR';
export const SUPPLIER_EMPLOYEES_SET_AS_MAIN_CONTACT = 'SUPPLIER_EMPLOYEES_SET_AS_MAIN_CONTACT';
export const SUPPLIER_EMPLOYEES_SET_AS_IMPORT_CONTACT = 'SUPPLIER_EMPLOYEES_SET_AS_IMPORT_CONTACT';
export const SUPPLIER_EMPLOYEES_CHANGE_POSITION = 'SUPPLIER_EMPLOYEES_CHANGE_POSITION';
export const SUPPLIER_EMPLOYEES_CHANGE_TAGS = 'SUPPLIER_EMPLOYEES_CHANGE_TAGS';
export const SUPPLIER_EMPLOYEES_REQUEST = 'SUPPLIER_EMPLOYEES_REQUEST';
export const SUPPLIER_EMPLOYEES_RECEIVED = 'SUPPLIER_EMPLOYEES_RECEIVED';
export const SUPPLIER_EMPLOYEES_RESET_BONUS_CONDITION_ERROR = 'SUPPLIER_EMPLOYEES_RESET_BONUS_CONDITION_ERROR';

const receiveSupplierEmployees = (employees: Employee[]) => {
    return {
        type: SUPPLIER_EMPLOYEES_RECEIVED,
        employees,
    };
};

export const fetchSupplierEmployees = (supplierId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_EMPLOYEES_REQUEST, supplierId });
        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Employees.getEmployees(supplierId);
        if (response.success) {
            dispatch(receiveSupplierEmployees(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const addEmployee = (supplierId: number, employee: Employee) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_EMPLOYEES_ADD, employee });

        if (!employee.id) {
            return;
        }

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Employees.AddEmployee(
            supplierId,
            employee.id,
            employee.position?.id
        );
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeAdded')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const removeEmployee = (
    supplierId: number,
    employeeId: number,
    nextMainContactId?: number,
    nextBonusContactId?: number,
    nextImportContactId?: number
) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: SUPPLIER_EMPLOYEES_REMOVE });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Employees.deleteEmployee(
            supplierId,
            employeeId,
            {
                reassignMainContactId: nextMainContactId,
                reassignBonusConditionsContactId: nextBonusContactId,
                reassignImportContactId: nextImportContactId,
            }
        );

        if (response.success) {
            dispatch({
                type: SUPPLIER_EMPLOYEES_REMOVE_SUCCESS,
                employeeId,
                nextMainContactId,
                nextBonusContactId,
                nextImportContactId,
            });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeRemoved')));
            if (nextMainContactId) {
                setEmployeeAsMainContact(supplierId, nextMainContactId)(dispatch, getState, { api });
            }
        } else if (response.error?.title === ErrorMessage.ContactIsAssignedToBonusCondition) {
            dispatch({
                type: SUPPLIER_EMPLOYEES_REMOVE_FAILED_WITH_BONUS_CONDITION_ERROR,
            });
        } else {
            dispatch({ type: SUPPLIER_EMPLOYEES_REMOVE_FAILED });
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const setEmployeeAsMainContact = (supplierId: number, employeeId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const employee = getState().contactsAndOrganizations.suppliers.supplierDetail.employees.employees?.find(
            employee => employee.id === employeeId
        );
        if (!employee) {
            return;
        }

        dispatch({ type: SUPPLIER_EMPLOYEES_SET_AS_MAIN_CONTACT, employeeId });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Employees.updateEmployee(
            supplierId,
            employeeId,
            { isMainContact: true, positionId: employee.position?.id, tagIds: employee.tags?.map(tag => tag.id) ?? [] }
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeSetAsMainContact')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const setEmployeeAsImportContact = (supplierId: number, employeeId: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const employee = getState().contactsAndOrganizations.suppliers.supplierDetail.employees.employees?.find(
            employee => employee.id === employeeId
        );
        if (!employee) {
            return;
        }

        dispatch({ type: SUPPLIER_EMPLOYEES_SET_AS_IMPORT_CONTACT, employeeId });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Employees.updateEmployee(
            supplierId,
            employeeId,
            {
                isImportContact: true,
                isMainContact: employee.isMainContact,
                positionId: employee.position?.id,
                tagIds: employee.tags?.map(tag => tag.id) ?? [],
            }
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeSetAsImportContact')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const changeEmployeePosition = (supplierId: number, employeeId: number, position: Position | null) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const employee = getState().contactsAndOrganizations.suppliers.supplierDetail.employees.employees?.find(
            employee => employee.id === employeeId
        );
        if (!employee) {
            return;
        }

        dispatch({ type: SUPPLIER_EMPLOYEES_CHANGE_POSITION, employeeId, position });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Employees.updateEmployee(
            supplierId,
            employeeId,
            {
                positionId: position?.id,
                tagIds: employee.tags?.map(tag => tag.id) ?? [],
                isMainContact: employee.isMainContact,
            }
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeChangedPosition')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const changeEmployeeTags = (customerId: number, employeeId: number, tags: Tag[]) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const employee = getState().contactsAndOrganizations.suppliers.supplierDetail.employees.employees?.find(
            employee => employee.id === employeeId
        );
        if (!employee) {
            return;
        }

        dispatch({ type: SUPPLIER_EMPLOYEES_CHANGE_TAGS, employeeId, tags });

        const response = await api.contactsAndOrganizations.Supplier.SupplierDetail.Employees.updateEmployee(
            customerId,
            employeeId,
            {
                tagIds: tags.map(tag => tag.id),
                positionId: employee.position?.id,
                isMainContact: employee.isMainContact,
            }
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.employeeTagsAssigned')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const resetBonusConditionError = () => {
    return { type: SUPPLIER_EMPLOYEES_RESET_BONUS_CONDITION_ERROR };
};
